import React, { useEffect, useState } from 'react';
import { FormError, AddressSearchPanel } from 'components';
import { Address } from 'types';
import { Button, TextInput, Select, SelectItem } from 'carbon-components-react';

const stateListItems: { value: string | null; label: string }[] = [
  {
    label: 'ACT',
    value: 'ACT'
  },
  {
    label: 'NSW',
    value: 'NSW'
  },
  {
    label: 'NT',
    value: 'NT'
  },
  {
    label: 'QLD',
    value: 'QLD'
  },
  {
    label: 'SA',
    value: 'SA'
  },
  {
    label: 'TAS',
    value: 'TAS'
  },
  {
    label: 'VIC',
    value: 'VIC'
  },
  {
    label: 'WA',
    value: 'WA'
  },
  {
    label: 'Other',
    value: 'OT'
  }
];

const PostalAddress: React.FC<Props> = ({
  defaultAddress,
  onChangeAddress,
  onBlurAddress,
  touched,
  errors
}) => {
  const [isAuto, setIsAuto] = useState(true);
  const [address, setAddress] = useState(
    defaultAddress || {
      line1: '',
      line2: '',
      locality: '',
      state: '',
      postcode: ''
    }
  );
  const handleChange = (key: string, value: string) => {
    if (address) {
      const newAddress = {
        ...address,
        [key]: value
      };
      setAddress(newAddress);
      onChangeAddress(newAddress, key);
    }
  };

  const handleBlur = (key: string, value: string) => {
    typeof onBlurAddress === 'function' && onBlurAddress(key, value);
  };

  const getAddressString = (address: any) => {
    if (address) {
      const addressPart1Arr = [];
      const addressPart2Arr = [];

      address?.line1 && addressPart1Arr.push(address.line1);
      address?.line2 && addressPart1Arr.push(address.line2);
      address?.locality && addressPart2Arr.push(address.locality);
      address?.state && addressPart2Arr.push(address.state);
      address?.postcode && addressPart2Arr.push(address.postcode);

      const addressPart1 = addressPart1Arr.join(', ');
      const addressPart2 = addressPart2Arr.join(' ');

      return `${addressPart1 !== '' ? `${addressPart1}, ` : ''}${addressPart2}`;
    }
  };

  const parseAddressString = (addString: string) => {
    const arr = addString.split(', ');
    let line1 = '';
    let line2 = '';
    let locality = '';
    let state = '';
    let postcode = '';

    if (arr.length >= 2) {
      const lastPartArr = arr[arr.length - 1].split(' ');
      if (lastPartArr.length > 3) {
        locality = lastPartArr.slice(0, lastPartArr.length - 2).join(' ');
        state = lastPartArr[lastPartArr.length - 2];
        postcode = lastPartArr[lastPartArr.length - 1];
      } else {
        locality = lastPartArr[0];
        state = lastPartArr[1];
        postcode = lastPartArr[2];
      }

      if (arr.length === 2) {
        line1 = arr[0];
      } else {
        line1 = arr.slice(0, arr.length - 2).join(', ');
        line2 = arr[arr.length - 2];
      }
    }

    const newAddress = {
      line1,
      line2,
      locality,
      state,
      postcode
    };

    setAddress(newAddress);
    onChangeAddress(newAddress, 'fullAddress');
  };

  useEffect(() => {
    defaultAddress && setAddress(defaultAddress);
  }, [defaultAddress]);

  return (
    <>
      {isAuto ? (
        <>
          <AddressSearchPanel
            labelText="Postal address"
            defaultInputValue={getAddressString(address)}
            onChangeInput={(value: string) => parseAddressString(value)}
            onBlurInput={(value: string) => handleBlur('fullAddress', value)}
          />
          {touched?.fullAddress && errors?.fullAddress ? (
            <div className="address-form-error-container">
              <FormError>{errors?.fullAddress}</FormError>
            </div>
          ) : null}
          <Button
            kind="ghost"
            className="postal-address-btn"
            onClick={() => setIsAuto(false)}
          >
            Enter address manually
          </Button>
        </>
      ) : (
        <>
          <section className="postal-address-form-item">
            <TextInput
              id="postal-address-line1"
              name="postal-address-line1"
              placeholder=""
              labelText="Postal address"
              onChange={(evt) => handleChange('line1', evt.target.value)}
              onBlur={(evt) => handleBlur('line1', evt.target.value)}
              defaultValue={address.line1}
            />
            {touched?.line1 && errors?.line1 ? (
              <FormError>{errors?.line1}</FormError>
            ) : null}
            <TextInput
              placeholder="Address line 2 (optional)"
              id="postal-address-line2"
              name="postal-address-line2"
              labelText=""
              onChange={(evt) => handleChange('line2', evt.target.value)}
              onBlur={(evt) => handleBlur('line2', evt.target.value)}
              defaultValue={address.line2}
            />
            {touched?.line2 && errors?.line2 ? (
              <FormError>{errors?.line2}</FormError>
            ) : null}
          </section>
          <section className="postal-address-form-item">
            <TextInput
              id="postal-address-locality"
              name="postal-address-locality"
              placeholder=""
              labelText="Suburb/Town/City"
              onChange={(evt) => handleChange('locality', evt.target.value)}
              onBlur={(evt) => handleBlur('locality', evt.target.value)}
              defaultValue={address.locality}
            />
            {touched?.locality && errors?.locality ? (
              <FormError>{errors?.locality}</FormError>
            ) : null}
          </section>
          <section className="postal-address-form-item postal-address-form-item-state">
            <div className="postal-address-state-select-container">
              <Select
                id="postal-address-state"
                name="postal-address-state"
                placeholder="State"
                className="postal-address-state"
                value={address.state}
                labelText={'State'}
                onChange={(evt) => handleChange('state', evt.target.value)}
                onBlur={(evt) => handleBlur('state', evt.target.value)}
              >
                <SelectItem text="" value="" />
                {stateListItems.map((item: any, index: number) => {
                  return (
                    <SelectItem
                      key={`state-${index}`}
                      text={item.label}
                      value={item.value}
                    />
                  );
                })}
              </Select>
              {touched?.state && errors?.state ? (
                <FormError>{errors?.state}</FormError>
              ) : null}
            </div>
            <div className="postal-address-postcode-input-container">
              <TextInput
                id="postal-address-postcode"
                name="postal-address-postcode"
                placeholder=""
                labelText="Postcode"
                onChange={(evt) => handleChange('postcode', evt.target.value)}
                onBlur={(evt) => handleBlur('postcode', evt.target.value)}
                defaultValue={address.postcode}
              />
              {touched?.postcode && errors?.postcode ? (
                <FormError>{errors?.postcode}</FormError>
              ) : null}
            </div>
          </section>
          <Button
            kind="ghost"
            className="postal-address-btn"
            onClick={() => setIsAuto(true)}
          >
            Use address search instead
          </Button>
        </>
      )}
    </>
  );
};

type Props = {
  defaultAddress?: Address;
  onChangeAddress: Function;
  onBlurAddress?: Function;
  touched?: any;
  errors?: any;
};
export default React.memo(PostalAddress);
